/* eslint-disable max-len */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { DrawerProps } from '@mui/material';
import { css } from '@emotion/react';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import Icons from '@bbkAdminUtils/icons';
import {
  ConfirmationDialog,
  DialogActions,
  DialogContent,
  DialogError,
  DialogTitle,
} from '@bbkAdminComponents/editor-page-overlay/dialog';
import { Label } from '@bbkAdminComponents/inputs/label/label';
import TimePicker from '@bbkAdminComponents/popovers/time-picker';
import {
  selectAppId,
  selectCompanyInternalName,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import type { CustomerReportOpts } from '@bbkAdminUtils/api-client/reports-api';
import { useExportCustomersData } from '@bbkAdminRedux/rtkq/reports.slice';
import GenericSelect from '@bbkAdminComponents/select/select';
import { segmentCustomEvent } from '@bbkAdminUtils/analytics';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';

type Props = Required<Pick<DrawerProps, 'open'>> & {
  onClose: () => void;
  reportOpts?: Partial<CustomerReportOpts>;
};

type formatItem = {
  label: 'CSV' | 'JSON';
  value: 'CSV' | 'JSON';
};
const formatOptions: formatItem[] = [
  {
    label: 'CSV',
    value: 'CSV',
  },
  {
    label: 'JSON',
    value: 'JSON',
  },
];

export const ExportCustomerDataDialog: React.FC<Props> = (props) => {
  const { open, onClose, reportOpts = {} } = props;
  const timePicker = useSelectDatePicker();
  const appId = useSelector(selectAppId);
  const companyId = useSelector(selectCompanyInternalName);
  const user = useSelector(selectUser);
  const [error, setError] = useState();
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportFormat, setExportFormat] = useState<formatItem>(
    formatOptions[0] as formatItem
  );

  const userEmail = user?.email;
  const exportCustomersData = useExportCustomersData();

  function exportData() {
    setExportInProgress(true);
    setError(undefined);

    const pageCustomerReportOpts: CustomerReportOpts = {
      start: timePicker.startDate,
      end: timePicker.endDate,
      format: exportFormat.value,
      ...reportOpts,
    };

    segmentCustomEvent('Customers Page Enhanced Export', {
      ...pageCustomerReportOpts,
      appId,
      companyId,
    });

    BlackToastTrigger({
      content: `Export generation in progress. This can take several minutes. Leave the export dialog open to save directly. An additional link will be emailed to ${userEmail} as well. Closing the export dialog will not stop the email.`,
      options: { autoClose: 8000 },
    });

    return exportCustomersData(pageCustomerReportOpts, {
      appId,
      companyId,
      returnDownloadUrl: true,
    })
      .then((downloadUrl) => {
        if (open) {
          window.location.href = downloadUrl as string;
          onClose();
        }
      })
      .catch((e: Error) => {
        console.error(e.message);
      })
      .finally(() => {
        setExportInProgress(false);
      });
  }

  const copy = {
    title: 'Generate customer export',
    content: `Exports may take a few minutes to generate depending on date range in question. Leave this dialog open to save a copy directly. Please check your email (${userEmail}) for a download link as well.`,
    button: 'Generate export',
    buttonActive: 'Generating export...',
  };

  return (
    <ConfirmationDialog open={open}>
      <DialogTitle>
        <div className="bbk-brand-management-conf-dialog-title-text">
          {copy.title}
        </div>
        <ButtonV2 circle autoFocus onClick={onClose}>
          {Icons.closeProper}
        </ButtonV2>
      </DialogTitle>
      <DialogContent
        css={css`
          .timeAgoButton {
            width: auto;
            border-color: rgba(0, 0, 0, 10%);
          }
        `}
      >
        <Label id="timeRange" label="Time range" />
        <TimePicker
          hidePicker
          readOnly
          className="tw-mb-1 !tw-inline-block tw-rounded tw-border tw-border-cbrneutral-300"
        />
        <Label id="format" label="Export format" />
        <div css={{ maxWidth: '50%' }}>
          <GenericSelect
            isSearchable={false}
            options={formatOptions}
            value={exportFormat}
            onChange={(value) => setExportFormat(value as formatItem)}
          />
        </div>
        <div css={{ fontSize: 12, marginTop: '1rem' }}>{copy.content}</div>
      </DialogContent>
      {error && <DialogError>{error}</DialogError>}
      <DialogActions>
        <ButtonV2 onClick={exportData} success disabled={exportInProgress}>
          {exportInProgress ? (
            <>
              {Icons.loaderSW6} {copy.buttonActive}
            </>
          ) : (
            copy.button
          )}
        </ButtonV2>
      </DialogActions>
    </ConfirmationDialog>
  );
};
